import { z } from 'zod';
/**
 * **Contrats d'entrée de `CDC-2` — le cours, son tableau, sa diffusion, sa visio.**
 *
 * Écrits une fois ici, ils valident l'entrée de l'API et valideraient les mêmes
 * formulaires côté front sans être réécrits (`DOCTRINE.md` §4). `strictObject`
 * partout : une clé inconnue produit un 400 visible, elle ne disparaît pas en
 * silence — asymétrie voulue avec la sortie, où un champ non déclaré est retiré
 * sans rien dire (D-021).
 *
 * **Chaque champ porte un `.describe()`** — seul `.describe()` atteint OpenAPI,
 * donc le client généré, donc l'autocomplétion de qui écrira un front (D-024).
 * Le motif interne d'une borne reste en commentaire : il regarde qui lit le code,
 * pas qui appelle l'API.
 */
/** Identifiant opaque d'une ligne. Jamais interprété côté client. */
const identifiant = z.string().trim().min(1).max(64);
/**
 * **Le placement d'un élément sur la grille, en points A4.**
 *
 * « L'origine de la grille est la même pour tous les tableaux : c'est une **constante
 * du produit**, et elle ne se stocke pas » (Q342, Q344). Ces nombres sont donc lus par
 * tous les postes dans le même repère, sans que rien ne se synchronise, et le produit
 * n'a aucune origine à transporter.
 */
const coordonnee = z.number().finite();
const dimension = z.number().finite().positive().max(100_000);
/**
 * **Un nombre tel qu'un FORMULAIRE le livre — D-072, troisième aide de cette famille.**
 *
 * « Un envoi multipart **n'a qu'un type de valeur** » : le transport livre `'120.5'`,
 * une chaîne, que `z.number()` refuse — et la route rendrait `400` **sur un corps
 * parfaitement formé**. D-072 a payé ce défaut sur un booléen et sur une liste ; c'est
 * le même canal, le même schéma écrit « comme si le corps était du JSON », sur un
 * troisième type.
 *
 * **Elle reste dans ce fichier**, et D-072 dit pourquoi : ses deux sœurs vivent dans le
 * contrat de `CDC-5`, et « une aide à un appelant unique est une indirection, pas une
 * factorisation ». Celle-ci a **un** appelant, le dépôt d'une image. Elles se
 * rassembleront le jour où l'une d'elles en aura deux.
 *
 * **Elle refuse ce qui n'est pas un nombre**, plutôt que de le ramener à zéro : une
 * position silencieusement nulle poserait l'image en haut de la première page sans que
 * rien ne le dise.
 */
const nombreDeFormulaire = (borne) => z.preprocess((valeur) => {
    if (typeof valeur !== 'string')
        return valeur;
    const lu = Number(valeur);
    return Number.isFinite(lu) ? lu : valeur;
}, borne);
/**
 * Titre d'un cours. **Obligatoire des trois chemins de création** (Q180, Q191).
 *
 * Aucune borne haute n'est écrite au corpus ; celle-ci empêche qu'un corps
 * énorme occupe le processus, elle ne borne pas une phrase.
 */
const titre = z.string().trim().min(1).max(200);
/**
 * Description d'un cours. **Obligatoire à la création, comme le titre** (Q180,
 * Q191, F-13 critère 5), et **classée sensible** (Q192, Q196).
 *
 * **Lecture retenue et signalée, parce que `CDC-2` se contredit sur ce point.**
 * F-13 écrit « titre et description sont **obligatoires** à la création, des
 * trois chemins » et son critère 5 nomme l'erreur `TITRE_ET_DESCRIPTION_OBLIGATOIRES` ;
 * F-14 écrit en passant « le patron du cours, qui porte un titre obligatoire et
 * une **description facultative** ». C'est F-13 qui l'emporte : elle est la
 * fonctionnalité qui possède la création, elle porte deux renvois
 * d'interrogatoire, et elle nomme un code d'erreur — là où la mention de F-14
 * est une analogie servant à justifier la facultativité d'un champ **de la
 * demande**. À confirmer par le porteur.
 */
const description = z.string().trim().min(1).max(4000);
// ---------------------------------------------------------------------------
// F-13 — création et modification d'un cours
// ---------------------------------------------------------------------------
/** `POST /api/v1/cours` — F-13. */
export const creationCoursSchema = z.strictObject({
    titre: titre.describe('Titre du cours. Obligatoire, et modifiable ensuite par le tuteur, un ' +
        'orgadmin sur les cours portant sa structure, et le compte qui voit tout.'),
    description: description.describe('Description du cours. Obligatoire à la création, et classée sensible : ' +
        'elle suit le cloisonnement du cours, sans champ restreint.'),
    elevesIds: z
        .array(identifiant)
        .min(1)
        .max(50)
        .describe('Les élèves du cours. Une relation avec le tuteur est le préalable pour ' +
        'chacun, y compris quand l’appel vient du compte qui voit tout. Le ' +
        'plafond d’élèves effectif est le plus bas du réglage de la structure ' +
        'et du réglage du parc.'),
    tuteurId: identifiant
        .nullish()
        .describe('Le tuteur qui tiendra le cours. À omettre quand l’appelant est lui-même ' +
        'ce tuteur ; obligatoire quand l’appel vient du compte qui voit tout ou ' +
        'du secret d’une structure, qui ne sont pas des tuteurs.'),
    organizationId: identifiant
        .nullish()
        .describe('La structure qui porte le cours, c’est-à-dire celle du tuteur sur ce ' +
        'cours. À omettre quand le tuteur n’a qu’un rattachement actif ; ' +
        'obligatoire quand il en a plusieurs, puisqu’il choisit alors laquelle ' +
        'porte le cours.'),
});
/** `PATCH /api/v1/cours/:id` — F-13. */
export const modificationCoursSchema = z
    .strictObject({
    titre: titre.nullish().describe('Nouveau titre. Omis, il ne change pas.'),
    description: description
        .nullish()
        .describe('Nouvelle description. Omise, elle ne change pas.'),
})
    // Un corps vide serait un appel sans objet : il écrirait une ligne d'audit
    // pour une modification qui n'a rien modifié.
    .refine((corps) => corps.titre != null || corps.description != null, 'Au moins un des deux champs doit être fourni.');
/** `POST /api/v1/cours/:id/participants` — F-16. */
export const ajoutParticipantSchema = z.strictObject({
    eleveId: identifiant.describe('L’élève à ajouter. Deux gardes, et ce sont des gardes et non des ' +
        'déclencheurs : la relation doit exister — l’ajout ne la crée pas au ' +
        'passage — et le plafond d’élèves ne doit pas être atteint.'),
});
// ---------------------------------------------------------------------------
// F-14 — la demande de cours
// ---------------------------------------------------------------------------
/**
 * `POST /api/v1/demandes` — F-14.
 *
 * **Les deux champs de texte n'ont aucune longueur maximum au corpus** (Q555,
 * patron du pense-bête). La borne posée ici est une borne de transport, pas une
 * règle : elle empêche un corps énorme, elle ne borne pas ce qu'un élève a à
 * dire de ce qu'il n'arrive pas à faire.
 */
export const creationDemandeSchema = z.strictObject({
    tuteurId: identifiant.describe('Le tuteur sollicité. Une relation est le préalable, et le plafond ' +
        'd’élèves du cours visé s’applique déjà à ce stade.'),
    conversationId: identifiant.describe('La conversation où la demande vit. C’est sur elle que se lit le ' +
        'cloisonnement des deux champs de texte : quiconque n’est pas ' +
        'participant de cette conversation ne les reçoit pas.'),
    sujet: z
        .string()
        .trim()
        .min(1)
        .max(4000)
        .describe('Ce que l’élève veut travailler. OBLIGATOIRE. Aucune longueur maximum ' +
        'n’est une règle du produit.'),
    blocage: z
        .string()
        .trim()
        .min(1)
        .max(4000)
        .nullish()
        .describe('Ce que l’élève n’arrive pas à faire. Facultatif. Aucune longueur ' +
        'maximum n’est une règle du produit.'),
});
/**
 * `POST /api/v1/demandes/:id/reponse` — F-14.
 *
 * **Le refus est sec, sans motif** : aucun champ de motif n'existe, et la portée
 * de Q299 porte sur le refus et jamais sur la demande (Q555).
 *
 * **Le titre et la description sont ceux que le TUTEUR saisit**, et aucun des
 * deux champs de la demande n'y est recopié (F-14 critère 6, U-17).
 */
export const reponseDemandeSchema = z
    .strictObject({
    reponse: z
        .enum(['acceptee', 'refusee'])
        .describe('La décision du tuteur. Un refus ne porte aucun motif : le produit n’en ' +
        'a pas de champ.'),
    coursId: identifiant
        .nullish()
        .describe('À l’acceptation : le cours déjà commencé auquel l’élève est ajouté. ' +
        'Omis, un cours neuf est créé, et le titre et la description sont ' +
        'alors requis.'),
    titre: titre
        .nullish()
        .describe('Titre du cours neuf, saisi par le tuteur. Requis à l’acceptation sans ' +
        '« coursId ». Aucun des deux champs de la demande n’y est recopié.'),
    description: description
        .nullish()
        .describe('Description du cours neuf, saisie par le tuteur. Même régime.'),
})
    .refine((corps) => corps.reponse === 'refusee' ||
    corps.coursId != null ||
    (corps.titre != null && corps.description != null), 'Une acceptation sans « coursId » exige « titre » et « description ».');
// ---------------------------------------------------------------------------
// F-21 à F-25 — le tableau
// ---------------------------------------------------------------------------
/**
 * Un élément que l'on pose sur le tableau : un tracé ou un pense-bête.
 *
 * **Le pense-bête est un objet métier, pas un outil** (Q352) : un rectangle
 * coloré portant un texte, une couleur, son auteur et sa position, et rien
 * d'autre. Ni état fait ou pas fait, ni destinataire — aucune règle du produit
 * ne les consommerait.
 *
 * **L'auteur n'est PAS dans ce corps**, et c'est une règle de sécurité : il
 * vient de la session, jamais du corps. Le déclarer ici laisserait n'importe
 * quel présent signer le tracé de quelqu'un d'autre.
 *
 * **L'IDENTITÉ, ELLE, EST DANS CE CORPS, ET C'EST L'INVERSE DE L'AUTEUR.**
 * L'auteur est une affirmation sur une personne : la laisser au client, c'est
 * laisser signer à la place d'un autre. L'identité n'affirme rien sur personne
 * — elle nomme une chose que le client vient de fabriquer, et **lui seul peut
 * la nommer**, parce qu'elle doit être la même chez tous les postes : celui qui
 * trace, ceux qui reçoivent par le canal, et le serveur.
 *
 * **Ce qu'elle ferme.** Sans elle, un poste ne sait désigner que les éléments
 * dont il a lui-même reçu la réponse d'écriture — donc jamais ceux d'un autre.
 * « Tout le monde efface le tracé de tout le monde » (Q350) était alors faux
 * dès qu'il y avait deux personnes.
 *
 * **Elle n'est pas une clé primaire.** Le serveur garde la sienne : celle-ci
 * vit dans une colonne à part, unique **dans un tableau**, ce qui borne à un
 * seul tableau ce qu'un client peut faire entrer en collision.
 */
export const elementDuTableauSchema = z.discriminatedUnion('genre', [
    z.strictObject({
        genre: z.literal('trace').describe('Un tracé, une forme ou du texte tapé.'),
        identiteClient: z
            .string()
            .trim()
            .min(1)
            .max(64)
            .describe('L’identité que le moteur de tableau a donnée à cet élément à sa ' +
            'naissance, chez son auteur. Elle voyage avec lui — par le canal ' +
            'temps réel comme par cette route —, elle est unique dans un ' +
            'tableau, et c’est par elle que n’importe quel présent le retire. ' +
            'Sans elle, un poste ne peut effacer que ce qu’il a lui-même posé.'),
        // Le moteur de tableau est isolé derrière une interface étroite (D-012) :
        // le produit ne connaît pas la forme interne d'un tracé, et il ne doit pas
        // la connaître, sans quoi son remplacement la ferait fuir partout.
        contenu: z
            .unknown()
            .describe('Le tracé tel que le moteur de tableau le décrit. Sa forme interne ' +
            'appartient au moteur : le produit la transporte et ne l’interprète pas.'),
    }),
    z.strictObject({
        genre: z.literal('pense-bete').describe('Un pense-bête.'),
        identiteClient: z
            .string()
            .trim()
            .min(1)
            .max(64)
            .describe('L’identité que le moteur de tableau a donnée à cet élément à sa ' +
            'naissance, chez son auteur. Elle voyage avec lui — par le canal ' +
            'temps réel comme par cette route —, elle est unique dans un ' +
            'tableau, et c’est par elle que n’importe quel présent le retire. ' +
            'Sans elle, un poste ne peut effacer que ce qu’il a lui-même posé.'),
        texte: z
            .string()
            .max(20000)
            .describe('Le texte du pense-bête, classé sensible. Aucune longueur maximum ' +
            'n’est une règle du produit.'),
        couleur: z
            .string()
            .trim()
            .min(1)
            .max(32)
            .describe('Sa couleur. Palette complète, aucune liste fermée.'),
        positionX: z.number().describe('Sa position horizontale sur le canevas.'),
        positionY: z.number().describe('Sa position verticale sur le canevas.'),
    }),
]);
/**
 * `POST /api/v1/tableaux/:id/contenu` — F-22, F-24, F-29, F-30.
 *
 * **C'est ici que la borne de perte de contenu se joue** (DD-10) : le poste qui
 * revient d'une coupure déclare depuis combien de temps il était coupé, et le
 * produit décide si sa version remplace celle du serveur. Sans ce champ, le
 * serveur ne peut pas distinguer un enregistrement ordinaire d'un retour de
 * coupure, et la borne n'existe pas.
 */
export const ecritureTableauSchema = z.strictObject({
    elements: z
        .array(elementDuTableauSchema)
        .min(1)
        .max(2000)
        .describe('Les éléments à poser sur le tableau.'),
    coupureSecondes: z
        .number()
        .int()
        .min(0)
        .max(86_400)
        .nullish()
        .describe('Durée de la coupure dont ce poste revient, en secondes. Omise sur un ' +
        'enregistrement ordinaire. Au-delà de trente secondes, la version de ce ' +
        'poste n’est retenue que si le serveur n’a rien reçu de personne d’autre ' +
        'depuis son départ ; sinon elle est refusée, le poste reçoit le tableau ' +
        'du serveur, et une ligne de refus est écrite.'),
});
/**
 * `PATCH /api/v1/tableaux/:id/contenu/:identite` — F-22, D-121.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * **LA MISE À JOUR D'UN ÉLÉMENT DÉJÀ POSÉ, ET C'EST LA REMONTÉE DE D-121.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * D-121, mot pour mot : « la route d'écriture **crée**, et aucune route ne met à
 * jour. **Déplacer ou redimensionner un élément déjà annoncé ne se conserve pas**, il
 * revient au rechargement là où il est né. » Et : « la surface écrite à la main avait
 * la même limite et **personne ne pouvait la voir**, faute d'outil de sélection. Le
 * moteur en apporte un, donc **la limite devient visible**. »
 *
 * **L'ADRESSAGE SE FAIT PAR L'IDENTITÉ DE NAISSANCE, ET PAR ELLE SEULE.** C'est celle
 * que le moteur donne à l'élément chez son auteur, celle que le canal transporte, et
 * la seule qu'un poste connaisse d'un élément **qu'il n'a pas posé lui-même**. Le
 * retrait accepte les deux identités parce qu'une relecture de scène nomme celle du
 * serveur ; ici, ce qui bouge est ce qui vient de bouger **dans le moteur**, et le
 * moteur ne connaît que la sienne.
 *
 * **AUCUN CHAMP D'IDENTITÉ DANS CE CORPS, ET C'EST UNE BORNE.** Elle est dans le
 * chemin. La porter aussi dans le corps ouvrirait la question de ce qu'on fait quand
 * les deux diffèrent — c'est-à-dire un renommage d'élément, que rien du corpus ne
 * demande et que la contrainte d'unicité `(boardId, clientId)` ferait échouer au
 * hasard des collisions.
 *
 * **NI L'AUTEUR NI LE GENRE NE CHANGENT.** L'auteur vient de la session à la création
 * et n'est pas relu ici : « l'auteur est **une trace, pas un droit** ». Le genre est
 * dans ce corps parce qu'il décide de la forme du reste, et le serveur refuse une
 * mise à jour qui changerait le genre d'un élément — un tracé ne devient pas un
 * pense-bête.
 */
export const miseAJourElementSchema = z.discriminatedUnion('genre', [
    z.strictObject({
        genre: z.literal('trace').describe('Un tracé, une forme ou du texte tapé.'),
        contenu: z
            .unknown()
            .describe('Le tracé tel que le moteur de tableau le décrit APRÈS le geste. Sa forme ' +
            'interne appartient au moteur : le produit la transporte et ne ' +
            'l’interprète pas. Il remplace le contenu précédent en entier — il n’y a ' +
            'aucune mise à jour partielle d’un tracé.'),
    }),
    z.strictObject({
        genre: z.literal('pense-bete').describe('Un pense-bête.'),
        texte: z
            .string()
            .max(20000)
            .describe('Son texte après le geste, classé sensible.'),
        couleur: z
            .string()
            .trim()
            .min(1)
            .max(32)
            .describe('Sa couleur après le geste.'),
        positionX: coordonnee.describe('Sa position horizontale après le geste.'),
        positionY: coordonnee.describe('Sa position verticale après le geste.'),
    }),
    z.strictObject({
        genre: z
            .literal('image')
            .describe('Une image posée. SES OCTETS NE CHANGENT JAMAIS — « l’original n’est pas ' +
            'conservé » (Q359), et « un PDF posé devient une image, définitivement » ' +
            '(Q364). Ce qui se met à jour est SON PLACEMENT, et lui seul : « une image ' +
            'posée se redimensionne, se recadre et se pivote » (Q362).'),
        positionX: coordonnee.describe('Sa position horizontale après le geste.'),
        positionY: coordonnee.describe('Sa position verticale après le geste.'),
        largeur: dimension.describe('Sa largeur après le geste, en points.'),
        hauteur: dimension.describe('Sa hauteur après le geste, en points.'),
    }),
]);
/**
 * `POST /api/v1/tableaux/:id/images` — F-24. Contrat de `CDC-2` §7.
 *
 * **Sept chemins d'entrée d'une image** (Q356), et `origine` les nomme tous.
 * L'origine n'est pas décorative : « photo » est le seul chemin que la caméra
 * occupée refuse (Q415, DD-19), et « adresse » est celui dont le corpus dit que
 * **le navigateur de la personne va chercher l'image, jamais le produit** (Q360)
 * — donc l'API reçoit ici des octets, dans ce cas comme dans les six autres, et
 * **elle n'émet aucun appel sortant vers une adresse fournie par une personne**
 * (F-24 critère 5).
 */
export const depotImageSchema = z.strictObject({
    type: z
        .enum(['image', 'pdf'])
        .describe('La nature du dépôt. Un PDF pose TOUTES ses pages, l’une sous l’autre, à ' +
        'la suite du contenu existant, chaque page devenant une image ' +
        'indépendante et définitive.'),
    origine: z
        .enum([
        'import',
        'collage',
        'glisser',
        'photo',
        'espace',
        'adresse',
        'capture',
    ])
        .describe('Par lequel des sept chemins l’image entre. « photo » est refusé tant que ' +
        'le canal se sert de la caméra. « adresse » désigne une image que le ' +
        'navigateur de la personne a déjà téléchargée : le produit n’émet aucun ' +
        'appel sortant vers une adresse fournie par une personne.'),
    /**
     * **OÙ L'IMAGE EST POSÉE, ET AVEC QUELLE TAILLE — Q362, ajouté le 20260829.**
     *
     * Sans ces quatre nombres, une image revenait au rechargement à une place que
     * personne n'avait choisie, et « une image posée **se redimensionne, se recadre et
     * se pivote** » n'était servi par aucun chemin du produit. **Ils sont facultatifs**
     * pour ne pas casser un appelant d'avant : absents, la surface pose l'image à la
     * suite du contenu, ce qui est le comportement qu'ils corrigent.
     */
    positionX: nombreDeFormulaire(coordonnee)
        .optional()
        .describe('Sa position horizontale sur le canevas, en points de la grille A4.'),
    positionY: nombreDeFormulaire(coordonnee)
        .optional()
        .describe('Sa position verticale sur le canevas, en points de la grille A4.'),
    largeur: nombreDeFormulaire(dimension)
        .optional()
        .describe('Sa largeur affichée, en points. Ce n’est pas celle de ses pixels : le tableau ' +
        'réduit à l’entrée, et la version conservée est la seule qui existe.'),
    hauteur: nombreDeFormulaire(dimension)
        .optional()
        .describe('Sa hauteur affichée, en points.'),
});
/**
 * `POST /api/v1/tableaux/:id/document/rangement` — F-25, second temps.
 *
 * **Envoi multipart** : les octets du document fabriqué voyagent à côté de ce corps,
 * comme sur le dépôt d'une image. Ce schéma ne porte donc que le seul champ que la
 * personne choisit.
 *
 * **AUCUN CHAMP DE NOM, ET C'EST UNE BORNE, PAS UN OUBLI.** Le nom est calculé par le
 * serveur — « la date du cours et l'instant de fabrication, **et rien d'autre** » (Q375) —
 * et un nom reçu ne serait même pas lu. Le déclarer inviterait à s'en servir.
 */
export const rangementDocumentSchema = z.strictObject({
    dossierId: z
        .string()
        .trim()
        .min(1)
        .max(64)
        .optional()
        .describe('Où ranger, dans VOTRE espace. La personne choisit le dossier au moment de ' +
        'la fabrication. Absent, le document va dans le dossier tenu par le ' +
        'produit qui récapitule tous les documents fabriqués, vit à la racine, et ' +
        'ne se renomme, ne se déplace, ne se supprime et ne se partage pas. Le ' +
        'second dossier tenu, celui des fichiers envoyés en conversation, est ' +
        'refusé : un document fabriqué n’a rien envoyé.'),
});
// ---------------------------------------------------------------------------
// F-31, F-32 — la visio et le partage d'écran
// ---------------------------------------------------------------------------
/**
 * `POST /api/v1/cours/:id/flux/:personneId/coupure` — F-31, F-32.
 *
 * **Couper retire, allumer expose** (DD-16). Il n'existe donc aucun champ
 * « allumer » : personne ne peut allumer le flux de quelqu'un d'autre, jamais,
 * pas même le tuteur, et une route qui porterait le verbe des deux côtés
 * inviterait à ouvrir la seconde moitié un jour.
 */
export const coupureFluxSchema = z.strictObject({
    flux: z
        .enum(['micro', 'camera', 'ecran'])
        .describe('Le flux à couper. Le tuteur du cours seul, et sur un autre que lui : ' +
        'couper son propre flux ne passe pas par cette route et ne laisse ' +
        'aucune ligne.'),
});
/**
 * **CE FICHIER NE DÉCLARE AUCUN SCHÉMA DE CORPS VIDE, ET C'EST UNE DÉCISION.**
 *
 * Huit des vingt et une routes du lot ne transportent **aucune donnée** : entrer,
 * sortir, terminer, répondre à la fenêtre d'inactivité, ouvrir un tableau, obtenir le
 * droit d'entrée au canal, signaler une chute, fabriquer le document. Toutes sont
 * identifiées par leur **chemin** et par **l'appelant**, et rien de plus.
 *
 * Un `strictObject` vide avait d'abord été posé sur chacune, pour qu'un appel portant
 * `{ motif: … }` sur la route de fin reçoive un 400 plutôt que d'être ignoré — le
 * produit déterminant seul le motif, à partir de qui appelle. **Il a été retiré** :
 * ces routes n'ont pas de corps, donc **elles n'en déclarent pas**, et le client
 * généré expose des fonctions sans argument de corps. Le contrat dit ainsi ce qui est
 * vrai, au lieu de décrire un corps qui doit rester vide.
 *
 * **Ce que le retrait coûte, écrit plutôt que passé sous silence** : un champ envoyé
 * sur l'une de ces huit routes est **ignoré en silence**, au lieu d'être refusé. C'est
 * le comportement ordinaire de HTTP, et il ne porte aucun risque de fuite — mais un
 * appelant qui croirait choisir le motif de fin d'un cours ne serait pas contredit.
 *
 * **La chute du canal est dans ce cas, et son motif est plus fort que la commodité** :
 * « le service extérieur n'appelle jamais le produit » (Q432), donc la ligne de chute
 * s'écrit sur ce que rapporte le poste qui revient — et **laisser le client déclarer
 * l'instant lui laisserait écrire dans une table de trace**. Il n'a donc rien à
 * envoyer.
 */
//# sourceMappingURL=cours.contract.js.map